import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Alert,
    Snackbar,
    alpha,
    useTheme,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Collapse,
    SelectChangeEvent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import globalVariableService, { GlobalVariableMapping, GlobalVariableMappingGroup } from '../services/globalVariableService';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';

// Interfaces atualizadas
interface MappingValue {
    id: number;
    value: string;
}

interface MappingResponse {
    id?: number;
    value?: string;
    [key: string]: any;
}

interface GlobalVariableMappingGroupExtended {
    variable_name: string;
    mappings: Record<string, MappingValue>;
}

const GlobalVariableMappings = () => {
    const theme = useTheme();
    const { showSuccess, showError } = useNotification();
    
    // Estados atualizados
    const [variables, setVariables] = useState<GlobalVariableMappingGroupExtended[]>([]);
    const [actTypes, setActTypes] = useState<string[]>([]);
    const [openNewVarDialog, setOpenNewVarDialog] = useState(false);
    const [openNewMappingDialog, setOpenNewMappingDialog] = useState(false);
    const [expandedVariable, setExpandedVariable] = useState<string | null>(null);
    const [editingCell, setEditingCell] = useState<{
        id: number;
        variableName: string;
        actType: string;
        value: string;
    } | null>(null);
    const [newVariableName, setNewVariableName] = useState('');
    const [newMapping, setNewMapping] = useState<{
        variableName: string;
        actType: string;
        value: string;
    }>({
        variableName: '',
        actType: '',
        value: ''
    });
    const [loading, setLoading] = useState(false);
    
    // Carregar dados
    useEffect(() => {
        loadVariables();
        loadActTypes();
    }, []);
    
    const loadVariables = async () => {
        try {
            setLoading(true);
            const response = await globalVariableService.getAllMappingsGrouped();
            console.log('Resposta da API (mapeamentos agrupados):', JSON.stringify(response, null, 2));
            
            // Transformar os dados para o formato esperado pelo componente
            const transformedVariables: GlobalVariableMappingGroupExtended[] = response.variables.map(variable => {
                const mappingsObj: Record<string, MappingValue> = {};
                
                // Converter o objeto de mapeamentos para o formato esperado
                Object.entries(variable.mappings).forEach(([actType, value]) => {
                    // Verificar se o valor é um objeto ou uma string
                    if (typeof value === 'object' && value !== null) {
                        // Se for um objeto, assumimos que tem id e value
                        mappingsObj[actType] = value as MappingValue;
                    } else {
                        // Se for uma string, criamos um objeto com id temporário
                        mappingsObj[actType] = {
                            id: -1, // ID temporário
                            value: value as string
                        };
                    }
                    console.log(`Processando mapeamento: actType=${actType}, value=`, value, 
                                `resultado=`, mappingsObj[actType]);
                });
                
                console.log(`Processando variável: ${variable.variable_name}, mappings:`, mappingsObj);
                
                // Não modificamos o nome da variável, mantemos exatamente como veio da API
                return {
                    variable_name: variable.variable_name,
                    mappings: mappingsObj
                };
            });
            
            // Ordenar variáveis alfabeticamente pelo nome
            const sortedVariables = [...transformedVariables].sort((a, b) => {
                if (!a.variable_name) return 1;
                if (!b.variable_name) return -1;
                return a.variable_name.localeCompare(b.variable_name, 'pt-BR');
            });
            
            console.log('Variáveis ordenadas alfabeticamente:', sortedVariables);
            setVariables(sortedVariables);
        } catch (error) {
            console.error('Erro ao carregar variáveis:', error);
            showError('Erro ao carregar variáveis');
        } finally {
            setLoading(false);
        }
    };
    
    const loadActTypes = async () => {
        try {
            const actTypes = await globalVariableService.getActTypes();
            console.log('Tipos de ato recebidos da API:', actTypes);
            
            // Verificar se há algum tipo de ato que não deveria estar na lista
            const response = await api.get('/flows-new');
            console.log('Fluxos carregados para verificação:', response.data);
            
            // Mapear fluxos disponíveis
            const availableFlowNames = response.data
                .filter((flow: any) => {
                    const isActive = flow.is_active === true;
                    const isAvailable = flow.available === undefined ? true : flow.available === true;
                    console.log(`Fluxo ${flow.id} - ${flow.name}: isActive=${isActive}, isAvailable=${isAvailable}, será incluído=${isActive && isAvailable}`);
                    return isActive && isAvailable;
                })
                .map((flow: any) => flow.name);
            
            console.log('Nomes de fluxos disponíveis:', availableFlowNames);
            
            // Verificar se há tipos de ato que não estão na lista de fluxos disponíveis
            const invalidActTypes = actTypes.filter(actType => !availableFlowNames.includes(actType));
            if (invalidActTypes.length > 0) {
                console.warn('Tipos de ato que não deveriam estar na lista:', invalidActTypes);
            }
            
            if (actTypes && actTypes.length > 0) {
                // Ordenar alfabeticamente
                const sortedActTypes = [...actTypes].sort((a, b) => a.localeCompare(b, 'pt-BR'));
                console.log('Tipos de ato ordenados:', sortedActTypes);
                setActTypes(sortedActTypes);
            } else {
                console.warn('Nenhum tipo de ato encontrado, usando valores padrão');
                // Valores padrão caso não tenha nenhum fluxo ativo (já ordenados)
                const defaultActTypes = [
                    'Doação',
                    'Escritura de Compra e Venda',
                    'Escritura de Doação',
                    'Escritura de Permuta',
                    'Inventário',
                    'Procuração',
                    'Testamento'
                ];
                console.log('Usando tipos de ato padrão:', defaultActTypes);
                setActTypes(defaultActTypes);
            }
        } catch (error) {
            console.error('Erro ao carregar tipos de ato:', error);
            // Valores padrão em caso de erro (já ordenados)
            const fallbackActTypes = [
                'Doação',
                'Escritura de Compra e Venda',
                'Escritura de Doação',
                'Escritura de Permuta',
                'Inventário',
                'Procuração',
                'Testamento'
            ];
            console.log('Usando tipos de ato de fallback:', fallbackActTypes);
            setActTypes(fallbackActTypes);
        }
    };
    
    const handleCreateVariable = async () => {
        try {
            if (!newVariableName.trim()) {
                showError('O nome da variável é obrigatório');
                return;
            }
            
            // Normalizar o nome da variável (remover chaves existentes e adicionar novas)
            const normalizedVarName = newVariableName.replace(/^\{|\}$/g, '');
            const formattedVarName = `{${normalizedVarName}}`;
            
            // Criar variável sem mapeamentos iniciais
            setVariables(prev => [...prev, {
                variable_name: formattedVarName,
                mappings: {}
            }]);
            
            setNewVariableName('');
            setOpenNewVarDialog(false);
            showSuccess('Variável criada com sucesso');
        } catch (error) {
            console.error('Erro ao criar variável:', error);
            showError('Erro ao criar variável');
        }
    };

    const handleCreateMapping = async () => {
        try {
            if (!newMapping.actType || !newMapping.value.trim()) {
                showError('Tipo de ato e valor são obrigatórios');
                return;
            }

            // Normalizar o nome da variável (remover chaves existentes e adicionar novas)
            const normalizedVarName = newMapping.variableName.replace(/^\{|\}$/g, '');
            
            const mapping: GlobalVariableMapping = {
                variable_name: `{${normalizedVarName}}`, // Adiciona chaves apenas uma vez
                act_type: newMapping.actType,
                value: newMapping.value
            };

            console.log('Novo mapeamento a ser criado:', mapping);

            await globalVariableService.createMapping(mapping);
            await loadVariables();
            setOpenNewMappingDialog(false);
            setNewMapping({ variableName: '', actType: '', value: '' });
            showSuccess('Mapeamento criado com sucesso');
        } catch (error) {
            console.error('Erro ao criar mapeamento:', error);
            showError('Erro ao criar mapeamento');
        }
    };

    const handleStartEditing = (id: number, variableName: string, actType: string, value: string) => {
        console.log('Iniciando edição:', { id, variableName, actType, value });
        setEditingCell({ id, variableName, actType, value });
    };

    const handleSaveEdit = async () => {
        if (!editingCell) return;

        console.log('Salvando edição:', editingCell);

        try {
            // Normalizar o nome da variável (remover chaves existentes e adicionar novas)
            const normalizedVarName = editingCell.variableName.replace(/^\{|\}$/g, '');
            
            const mapping: GlobalVariableMapping = {
                variable_name: `{${normalizedVarName}}`, // Adiciona chaves apenas uma vez
                act_type: editingCell.actType,
                value: editingCell.value
            };

            console.log('Mapeamento a ser salvo:', mapping);

            if (editingCell.id === -1) {
                // Se for um ID temporário, criar novo mapeamento
                await globalVariableService.createMapping(mapping);
            } else {
                // Se tiver ID válido, atualizar mapeamento existente
                await globalVariableService.updateMapping(editingCell.id, mapping);
            }

            await loadVariables();
            setEditingCell(null);
            showSuccess('Valor atualizado com sucesso');
        } catch (error) {
            console.error('Erro ao atualizar valor:', error);
            showError('Erro ao atualizar valor');
        }
    };

    const handleDelete = async (mappingId: number, variableName: string, actType: string) => {
        if (window.confirm('Tem certeza que deseja excluir este mapeamento?')) {
            try {
                console.log(`Tentando excluir mapeamento: variável=${variableName}, tipo=${actType}`);
                await globalVariableService.deleteMappingByVariableAndType(variableName, actType);
                showSuccess('Mapeamento excluído com sucesso!');
                loadVariables(); // Recarrega a lista após excluir
            } catch (error) {
                console.error('Erro ao excluir mapeamento:', error);
                if (error instanceof Error) {
                    showError(`Erro ao excluir mapeamento: ${error.message}`);
                } else {
                    showError('Erro ao excluir mapeamento');
                }
            }
        }
    };
    
    return (
        <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    Mapeamentos Globais de Variáveis
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenNewVarDialog(true)}
                    sx={{ borderRadius: 2 }}
                >
                    Nova Variável
                </Button>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 4 }}>
                Configure mapeamentos globais de variáveis que serão aplicados automaticamente em todas as questões e conclusões.
            </Typography>
            
            {variables.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Nenhum mapeamento global definido. Clique em "Nova Variável" para começar.
                </Alert>
            ) : (
                <List>
                    {variables.map((variable, index) => (
                        <Paper 
                            key={index}
                            elevation={0}
                            sx={{ 
                                mb: 2,
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                borderRadius: 2,
                                overflow: 'hidden'
                            }}
                        >
                            <ListItem 
                                button 
                                onClick={() => setExpandedVariable(
                                    expandedVariable === variable.variable_name ? null : variable.variable_name
                                )}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                    }
                                }}
                            >
                                <ListItemText 
                                    primary={
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Variável: {variable.variable_name}
                                        </Typography>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setNewMapping(prev => ({
                                                ...prev,
                                                variableName: variable.variable_name
                                            }));
                                            setOpenNewMappingDialog(true);
                                        }}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                    {expandedVariable === variable.variable_name ? (
                                        <ExpandLessIcon sx={{ ml: 1 }} />
                                    ) : (
                                        <ExpandMoreIcon sx={{ ml: 1 }} />
                                    )}
                                </ListItemSecondaryAction>
                            </ListItem>
                            
                            <Collapse in={expandedVariable === variable.variable_name}>
                                <Box sx={{ p: 2 }}>
                                    <TableContainer component={Paper} elevation={0} sx={{ mb: 2 }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 600 }}>Tipo de Ato</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Valor</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(variable.mappings).map(([actType, mapping], i) => (
                                                    <TableRow key={i}>
                                                        <TableCell>{actType}</TableCell>
                                                        <TableCell>
                                                            {editingCell?.variableName === variable.variable_name && 
                                                             editingCell?.actType === actType ? (
                                                                <TextField
                                                                    value={editingCell.value}
                                                                    onChange={(e) => setEditingCell(prev => ({
                                                                        ...prev!,
                                                                        value: e.target.value
                                                                    }))}
                                                                    size="small"
                                                                    fullWidth
                                                                />
                                                            ) : (
                                                                mapping.value
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {editingCell?.variableName === variable.variable_name && 
                                                             editingCell?.actType === actType ? (
                                                                <>
                                                                    <IconButton 
                                                                        color="primary"
                                                                        onClick={handleSaveEdit}
                                                                    >
                                                                        <SaveIcon />
                                                                    </IconButton>
                                                                    <IconButton 
                                                                        color="error"
                                                                        onClick={() => setEditingCell(null)}
                                                                    >
                                                                        <CancelIcon />
                                                                    </IconButton>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <IconButton 
                                                                        color="primary"
                                                                        onClick={() => handleStartEditing(
                                                                            mapping.id,
                                                                            variable.variable_name,
                                                                            actType,
                                                                            mapping.value
                                                                        )}
                                                                    >
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                    <IconButton 
                                                                        color="error"
                                                                        onClick={() => handleDelete(
                                                                            mapping.id,
                                                                            variable.variable_name,
                                                                            actType
                                                                        )}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Collapse>
                        </Paper>
                    ))}
                </List>
            )}
            
            {/* Modal para criar nova variável */}
            <Dialog 
                open={openNewVarDialog} 
                onClose={() => setOpenNewVarDialog(false)}
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>Nova Variável</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            label="Nome da Variável"
                            value={newVariableName}
                            onChange={(e) => setNewVariableName(e.target.value)}
                            fullWidth
                            helperText="Nome da variável sem as chaves. Ex: 'parte' para {parte}"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenNewVarDialog(false)}>Cancelar</Button>
                    <Button onClick={handleCreateVariable} variant="contained" color="primary">
                        Criar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal para adicionar novo mapeamento */}
            <Dialog 
                open={openNewMappingDialog} 
                onClose={() => setOpenNewMappingDialog(false)}
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>Novo Mapeamento</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Tipo de Ato</InputLabel>
                            <Select
                                value={newMapping.actType}
                                onChange={(e: SelectChangeEvent) => setNewMapping(prev => ({
                                    ...prev,
                                    actType: e.target.value
                                }))}
                                label="Tipo de Ato"
                            >
                                {actTypes.map((type, index) => (
                                    <MenuItem key={index} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Valor"
                            value={newMapping.value}
                            onChange={(e) => setNewMapping(prev => ({
                                ...prev,
                                value: e.target.value
                            }))}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenNewMappingDialog(false)}>Cancelar</Button>
                    <Button onClick={handleCreateMapping} variant="contained" color="primary">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GlobalVariableMappings; 